import { PropsWithChildren, useMemo } from 'react'
import styles from './SimulationRegistrationSideCardLayout.module.scss'
import ProducerLocation from 'components/producers/producer-location/ProducerLocation'
import { SimulationRegistrationSideCardLayoutProps } from 'components/simulation-registration-side-card-layout/types'
import { getCapacityState, getProducerLocation } from 'utils/producers'
import { useTranslation } from 'next-i18next/pages'
import classNames from 'classnames'
import { CapacityState } from 'types/producer'
import { useStoreSelector } from 'hooks/store'
import { useRouter } from 'next/router'
import { CldImage } from 'next-cloudinary'
import { LoadingSkeleton } from '@boltenergy-be/design-system'
import SideCardAction from 'components/simulation-registration-side-card-layout/components/side-card-action/SideCardAction'

const SimulationRegistrationSideCardLayout = ({
  producer,
  hideProducerImage,
  hideProducerLocation,
  className,
  headerClass,
  children,
  loading
}: PropsWithChildren<SimulationRegistrationSideCardLayoutProps>) => {
  // REDUX
  const { inputs, flows } = useStoreSelector((store) => store.customer)

  // i18n
  const { t } = useTranslation('producer')

  // Router
  const { locale } = useRouter()

  // Constants
  const capacityState = useMemo<CapacityState | undefined>(
    () => (producer ? getCapacityState(producer, inputs.simulation, flows.simulation.didSimulation) : undefined),
    [flows.simulation.didSimulation, inputs.simulation, producer]
  )

  return !loading && producer ? (
    <section className={styles.card}>
      {/* CARD HEADER */}
      {!hideProducerImage && (
        <figure className={classNames(styles['card-header'], headerClass)}>
          <CldImage
            fill
            className={styles['producer-image']}
            src={producer.images.profile?.url}
            alt={producer.name[locale]}
            placeholder="blur"
            blurDataURL={producer.images.profile?.url}
          />
          {!!capacityState?.almostSoldOut && <span className={styles.tag}>{t('status.almostSoldOut', 'Bijna uitverkocht')}</span>}
          {!hideProducerLocation && (
            <ProducerLocation {...producer} location={getProducerLocation(locale, producer?.location)} className={styles.location} />
          )}
        </figure>
      )}

      {/* CARD CONTENT */}
      <div className={classNames(styles['card-content-container'], { [styles['fully-boxed']]: hideProducerImage }, className)}>
        {children}
      </div>
    </section>
  ) : loading ? (
    <div className={classNames(styles.card, styles.skeleton)}>
      <LoadingSkeleton className="gap-none">
        <LoadingSkeleton.Rectangle width="100%" height={250} radius="none" className={styles['img-skeleton']} />
        <div className={classNames(styles['card-content-container'], 'gap-300 p-400 mt-none')}>
          <LoadingSkeleton.Paragraph lines={3} />
        </div>
      </LoadingSkeleton>
    </div>
  ) : (
    // TODO: error state because no producers were found
    <></>
  )
}

SimulationRegistrationSideCardLayout.Action = SideCardAction

export default SimulationRegistrationSideCardLayout
